/**
 * Capture session. Mini PRD: two input methods, one pipeline - camera
 * (multi-shot session for multi-page documents), gallery pick, file upload.
 *
 * The viewfinder uses the REAL camera when one is available (getUserMedia,
 * rear-facing on phones) and each shutter tap grabs a real frame - this is
 * the "test capture UX on real devices early" constraint made testable.
 * Without a camera (desktop, denied permission, CI) it falls back to the
 * simulated viewfinder. Gallery and file pickers offer the synthetic
 * fixture set, including one unreadable scan to demo the failure path.
 */
import { useEffect, useRef, useState, type ChangeEvent } from 'react';
import { useNavigation } from '@/hooks/useNavigationContext';
import {
  Box,
  Stack,
  Typography,
  Dialog,
  List,
  ListItemButton,
  ListItemText,
  ListItemAvatar,
} from '@mui/material';

import { Button } from '@karehero/llama/components/atoms/Button/Button';

import FlowScreen from './components/FlowScreen';
import {
  CameraIcon,
  GalleryIcon,
  FileUpIcon,
  SparklesIcon,
} from './components/LocalIcons';
import { palette } from './components/palette';
import { useCamera } from './components/useCamera';
import { useCaptureFlow } from './CaptureFlowContext';
import {
  galleryChoices,
  fileChoices,
  photoSessionDocument,
  makeUserDocument,
  type CapturedDocument,
  type CaptureMethod,
} from './fixtures';

type PickerKind = 'gallery' | 'file' | null;

/** Placeholder "snapped page" shown in simulated (no-camera) mode. */
const MockPage = () => (
  <Box
    sx={{
      width: '70%',
      height: '80%',
      backgroundColor: '#f4f1ea',
      borderRadius: 1,
      transform: 'rotate(-3deg)',
      padding: 2,
    }}
  >
    <Stack spacing={1}>
      <Box
        sx={{ width: '45%', height: 10, backgroundColor: palette.divider }}
      />
      {[...Array(6)].map((_, i) => (
        <Box
          key={i}
          sx={{
            width: `${88 - (i % 3) * 14}%`,
            height: 6,
            backgroundColor: '#dcd6c8',
          }}
        />
      ))}
    </Stack>
  </Box>
);

export const Capture = () => {
  const navigate = useNavigation();
  const {
    pages,
    addPage,
    removePage,
    selectDocument,
    startPhotoSession,
    autoOpenPicker,
    setAutoOpenPicker,
  } = useCaptureFlow();
  const [picker, setPicker] = useState<PickerKind>(null);
  const [reviewPage, setReviewPage] = useState<number | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const { videoRef, state: cameraState, captureFrame } = useCamera();

  // Fresh session on entry - clears any pages left from an earlier capture.
  // Journey prompts can deep-enter with a picker pre-opened.
  useEffect(() => {
    startPhotoSession();
    if (autoOpenPicker) {
      setPicker(autoOpenPicker);
      setAutoOpenPicker(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDeviceFile = (file: File | null) => {
    if (!file) return;
    processDocument(makeUserDocument(file), 'file');
  };

  const openPicker = (kind: Exclude<PickerKind, null>) => {
    // Blur the trigger so MUI's Dialog aria-hidden doesn't trap a focused
    // ancestor (Chrome a11y warning).
    (document.activeElement as HTMLElement | null)?.blur();
    setPicker(kind);
  };

  const processDocument = (doc: CapturedDocument, method: CaptureMethod) => {
    selectDocument(doc, method);
    setPicker(null);
    navigate('/capture/processing');
  };

  const finishPhotoSession = () => {
    processDocument(
      { ...photoSessionDocument, pageCount: pages.length },
      'photo',
    );
  };

  const lastFrame = [...pages].reverse().find((p) => p !== null) ?? null;
  const pageCount = pages.length;

  return (
    <FlowScreen isDark backTo='/care'>
      <Stack spacing={2} sx={{ height: '100%' }}>
        {/* Viewfinder */}
        <Box
          sx={{
            position: 'relative',
            borderRadius: 3,
            backgroundColor: '#000',
            aspectRatio: '3 / 4',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            overflow: 'hidden',
          }}
        >
          {/* Live camera stream (hidden until ready) */}
          <Box
            component='video'
            ref={videoRef}
            autoPlay
            playsInline
            muted
            sx={{
              position: 'absolute',
              inset: 0,
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              display: cameraState === 'live' ? 'block' : 'none',
            }}
          />

          {/* Corner brackets */}
          {[
            { top: 16, left: 16, borderTop: 2, borderLeft: 2 },
            { top: 16, right: 16, borderTop: 2, borderRight: 2 },
            { bottom: 16, left: 16, borderBottom: 2, borderLeft: 2 },
            { bottom: 16, right: 16, borderBottom: 2, borderRight: 2 },
          ].map((pos, i) => (
            <Box
              key={i}
              sx={{
                position: 'absolute',
                width: 36,
                height: 36,
                borderColor: '#e8b4a4',
                borderStyle: 'solid',
                borderWidth: 0,
                zIndex: 2,
                ...pos,
              }}
            />
          ))}

          {cameraState !== 'live' && pageCount === 0 && (
            <Stack spacing={1.5} sx={{ alignItems: 'center', px: 4 }}>
              <Box
                sx={{
                  width: 52,
                  height: 52,
                  borderRadius: '50%',
                  backgroundColor: 'rgba(255,255,255,0.15)',
                  color: '#fff',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <CameraIcon size={24} />
              </Box>
              <Typography
                sx={{ color: '#fff', fontSize: 14, textAlign: 'center' }}
              >
                Take a photo of a care document
              </Typography>
            </Stack>
          )}

          {cameraState !== 'live' && pageCount > 0 && <MockPage />}

          {pageCount > 0 && (
            <Box
              sx={{
                position: 'absolute',
                top: 20,
                right: 20,
                zIndex: 2,
                backgroundColor: 'rgba(0,0,0,0.7)',
                color: '#fff',
                borderRadius: 99,
                px: 1.5,
                py: 0.5,
                fontSize: 12,
                fontWeight: 700,
              }}
            >
              {pageCount} page{pageCount === 1 ? '' : 's'}
            </Box>
          )}

          {/* Page thumbnail strip - tap a page to review or remove it */}
          {pageCount > 0 && (
            <Stack
              direction='row'
              spacing={0.75}
              sx={{
                position: 'absolute',
                bottom: 12,
                left: 12,
                right: 12,
                zIndex: 2,
                overflowX: 'auto',
                paddingBottom: '2px',
              }}
            >
              {pages.map((thumb, i) => (
                <Box
                  key={i}
                  component='button'
                  aria-label={`Review page ${i + 1}`}
                  onClick={() => setReviewPage(i)}
                  sx={{
                    width: 34,
                    height: 46,
                    padding: 0,
                    borderRadius: 0.5,
                    border: '1.5px solid rgba(255,255,255,0.85)',
                    backgroundColor: '#f4f1ea',
                    overflow: 'hidden',
                    flexShrink: 0,
                    cursor: 'pointer',
                  }}
                >
                  {thumb ? (
                    <Box
                      component='img'
                      src={thumb}
                      alt={`Captured page ${i + 1}`}
                      sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                  ) : (
                    <Box sx={{ p: '4px' }}>
                      {[...Array(4)].map((_, j) => (
                        <Box
                          key={j}
                          sx={{
                            height: 3,
                            mb: '3px',
                            backgroundColor: '#dcd6c8',
                          }}
                        />
                      ))}
                    </Box>
                  )}
                </Box>
              ))}
            </Stack>
          )}
        </Box>

        {/* AI hint */}
        <Stack
          direction='row'
          spacing={0.75}
          sx={{
            alignItems: 'center',
            alignSelf: 'center',
            backgroundColor: palette.lavender,
            color: palette.lavenderInk,
            borderRadius: 99,
            padding: '4px 12px',
          }}
        >
          <SparklesIcon size={13} />
          <Typography sx={{ fontSize: 12 }}>
            Keni will extract the details automatically
          </Typography>
        </Stack>

        {pageCount > 0 && (
          <Typography
            sx={{
              color: 'rgba(255,255,255,0.75)',
              fontSize: 13,
              textAlign: 'center',
            }}
          >
            Long document? Keep snapping - one page per photo.
          </Typography>
        )}

        {/* Controls */}
        <Stack
          direction='row'
          sx={{ alignItems: 'center', justifyContent: 'space-evenly', py: 1 }}
        >
          <Box
            component='button'
            aria-label='Choose from gallery'
            onClick={() => openPicker('gallery')}
            sx={{
              width: 48,
              height: 48,
              borderRadius: 2,
              border: '1px solid rgba(255,255,255,0.35)',
              backgroundColor: 'transparent',
              color: '#fff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
            }}
          >
            <GalleryIcon size={20} />
          </Box>

          <Box
            component='button'
            aria-label='Take photo'
            onClick={() => addPage(captureFrame())}
            sx={{
              width: 72,
              height: 72,
              borderRadius: '50%',
              border: '4px solid rgba(255,255,255,0.9)',
              backgroundColor: palette.shutterOrange,
              cursor: 'pointer',
            }}
          />

          <Box
            component='button'
            aria-label='Upload a file'
            onClick={() => openPicker('file')}
            sx={{
              width: 48,
              height: 48,
              borderRadius: 2,
              border: '1px solid rgba(255,255,255,0.35)',
              backgroundColor: 'transparent',
              color: '#fff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
            }}
          >
            <FileUpIcon size={20} />
          </Box>
        </Stack>

        {pageCount > 0 && (
          <Button
            ariaLabel='Process captured pages'
            variant='primary'
            isFullWidth
            onPress={finishPhotoSession}
          >
            {`Done - process ${pageCount} page${pageCount === 1 ? '' : 's'}`}
          </Button>
        )}

        {lastFrame && (
          <Typography
            sx={{
              color: 'rgba(255,255,255,0.5)',
              fontSize: 11,
              textAlign: 'center',
            }}
          >
            Live camera frames captured - processed as the demo LPA for now.
          </Typography>
        )}
      </Stack>

      {/* Page review dialog: retake or remove a page mid-session */}
      <Dialog
        open={reviewPage !== null}
        onClose={() => setReviewPage(null)}
        fullWidth
      >
        <Box sx={{ p: 2 }}>
          <Typography sx={{ mb: 1 }}>
            Page {reviewPage !== null ? reviewPage + 1 : ''} of {pageCount}
          </Typography>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              backgroundColor: '#1c1c1e',
              borderRadius: 1,
              p: 1.5,
              mb: 2,
            }}
          >
            {reviewPage !== null && pages[reviewPage] ? (
              <Box
                component='img'
                src={pages[reviewPage] as string}
                alt={`Page ${reviewPage + 1} preview`}
                sx={{ maxWidth: '100%', maxHeight: 280, borderRadius: 0.5 }}
              />
            ) : (
              <Box
                sx={{
                  width: 140,
                  height: 190,
                  backgroundColor: '#f4f1ea',
                  borderRadius: 0.5,
                  p: 1.5,
                }}
              >
                {[...Array(7)].map((_, j) => (
                  <Box
                    key={j}
                    sx={{ height: 5, mb: '6px', backgroundColor: '#dcd6c8' }}
                  />
                ))}
              </Box>
            )}
          </Box>
          <Stack spacing={1}>
            <Button
              ariaLabel='Remove this page'
              variant='secondary'
              isFullWidth
              onPress={() => {
                if (reviewPage !== null) removePage(reviewPage);
                setReviewPage(null);
              }}
            >
              Remove this page (retake it after)
            </Button>
            <Button
              ariaLabel='Keep this page'
              variant='link'
              isFullWidth
              onPress={() => setReviewPage(null)}
            >
              Keep it
            </Button>
          </Stack>
        </Box>
      </Dialog>

      {/* Hidden input for real device files (any image or PDF) */}
      <Box
        component='input'
        ref={fileInputRef}
        type='file'
        accept={picker === 'gallery' ? 'image/*' : 'image/*,application/pdf'}
        aria-label='Choose a file from this device'
        onChange={(e: ChangeEvent<HTMLInputElement>) =>
          handleDeviceFile(e.target.files?.[0] ?? null)
        }
        sx={{ display: 'none' }}
      />

      {/* Mock pickers */}
      <Dialog open={picker !== null} onClose={() => setPicker(null)} fullWidth>
        <Box sx={{ p: 2 }}>
          <Typography sx={{ mb: 1 }}>
            {picker === 'gallery' ? 'Choose a photo' : 'Choose a file'}
          </Typography>
          <List>
            <ListItemButton onClick={() => fileInputRef.current?.click()}>
              <ListItemAvatar>
                <Box
                  sx={{
                    width: 40,
                    height: 56,
                    borderRadius: 0.5,
                    border: `1.5px dashed ${palette.mutedText}`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: palette.mutedText,
                  }}
                >
                  <FileUpIcon size={18} />
                </Box>
              </ListItemAvatar>
              <ListItemText
                primary='Choose from this device…'
                secondary='Your own file - demo understanding for now'
                sx={{ ml: 1 }}
              />
            </ListItemButton>
            {(picker === 'gallery' ? galleryChoices : fileChoices).map(
              (doc) => (
                <ListItemButton
                  key={doc.id}
                  onClick={() =>
                    processDocument(
                      doc,
                      picker === 'gallery' ? 'gallery' : 'file',
                    )
                  }
                >
                  <ListItemAvatar>
                    {doc.pageImages[0] ? (
                      <Box
                        component='img'
                        src={doc.pageImages[0]}
                        alt=''
                        sx={{
                          width: 40,
                          height: 56,
                          objectFit: 'cover',
                          objectPosition: 'top',
                          borderRadius: 0.5,
                          border: `1px solid ${palette.divider}`,
                          backgroundColor: '#fff',
                        }}
                      />
                    ) : (
                      <Box
                        sx={{
                          width: 40,
                          height: 56,
                          borderRadius: 0.5,
                          border: `1px solid ${palette.divider}`,
                          backgroundColor: '#e9e5da',
                          filter: 'blur(0.5px)',
                        }}
                      />
                    )}
                  </ListItemAvatar>
                  <ListItemText
                    primary={doc.fileName}
                    secondary={`${doc.fileSizeKb}kb${doc.willFail ? ' · blurry' : ''}`}
                    sx={{ ml: 1 }}
                  />
                </ListItemButton>
              ),
            )}
          </List>
        </Box>
      </Dialog>
    </FlowScreen>
  );
};

export default Capture;
